import { BlockProps } from '@/components/Block/Block';
import IconButton from '@/components/Redactie/IconButton/IconButton';
import ImagePickerModal from '@/components/Redactie/Modal/ImagePickerModal';
import ResponsiveImage from '@/components/ResponsiveImage/ResponsiveImage';
import { Dialog } from '@headlessui/react';
import { mdiArrowCollapse, mdiArrowExpand, mdiCloudUpload } from '@mdi/js';
import Icon from '@mdi/react';
import { motion, useReducedMotion } from 'framer-motion';
import { useEffect, useMemo, useRef, useState } from 'react';

import styles from './ChatImageBlock.module.sass';
import { ChatBlockProfilePicture } from '../ChatTextBlock/ChatTextBlock';

interface ChatImageBlockData {
    alt_text: string;
}

const ChatImageBlock = ({
    data,
    uuid,
    media,
    attachments,
    isEditable,
    isAnimated,
    activeChatCharacter,
    onContentHeightChanged,
    onFinished,
    onEdited,
}: BlockProps) => {
    const chatImageBlockRef = useRef(null);
    const shouldReduceMotion = useReducedMotion();
    const [isSending, setIsSending] = useState<boolean>(
        !isEditable && !!isAnimated,
    );
    const [blockData, setBlockData] = useState<ChatImageBlockData>({
        alt_text: data.alt_text ?? '',
    });
    const [showImagePickerModal, setShowImagePickerModal] = useState<boolean>(
        false,
    );
    const [imageFile, setImageFile] = useState<File | null>(attachments?.image);
    const imageFileBase64Url = useMemo(() => {
        if (!imageFile) return null;
        return URL.createObjectURL(imageFile);
    }, [imageFile, media?.image?.url]);
    const hasImage = !!imageFile || !!media?.image?.url;
    const [isFullScreen, setIsFullScreen] = useState<boolean>(false);

    useEffect(() => {
        setTimeout(() => {
            if (chatImageBlockRef.current) {
                setIsSending(false);
                onContentHeightChanged?.();
            }
        }, 2000);
        setTimeout(() => {
            if (chatImageBlockRef.current) onFinished?.();
        }, 6000);
    }, []);

    useEffect(() => {
        if (!isEditable) return;
        onEdited?.({
            uuid,
            data: JSON.parse(JSON.stringify(blockData)),
            attachments: imageFile
                ? {
                      image: imageFile,
                  }
                : {},
        });
    }, [blockData, imageFile]);

    return (
        <div className="relative">
            {!!activeChatCharacter && (
                <div className="absolute" aria-hidden>
                    <ChatBlockProfilePicture
                        className="absolute top-0 left-0"
                        name={activeChatCharacter.name}
                        isTyping={false}
                    />
                </div>
            )}
            <div
                ref={chatImageBlockRef}
                className={`${styles['chatimageblock']} ${
                    isEditable && styles['chatimageblock--editable']
                } ${isAnimated && styles['chatimageblock--animated']}`}
            >
                {isSending ? (
                    <div className="mr-auto rounded-2xl rounded-tl-md bg-slate-200 p-2 font-bold">
                        <p className="flex flex-col px-2 text-slate-900">
                            <span className="animate-pulse text-lg">
                                verstuurt een afbeelding...
                            </span>
                        </p>
                    </div>
                ) : (
                    <div className={`${styles['chatimageblock__bubble']}`}>
                        {isEditable ? (
                            <div className="relative">
                                <ResponsiveImage
                                    imageClassName={`${styles['chatimageblock__bubble__image']}`}
                                    responsiveImages={
                                        imageFileBase64Url
                                            ? undefined
                                            : media?.image?.responsive_images
                                    }
                                    alt={blockData.alt_text}
                                    src={
                                        imageFileBase64Url ?? media?.image?.url
                                    }
                                />
                                <div
                                    className={
                                        hasImage
                                            ? 'absolute left-4 bottom-4'
                                            : 'm-2'
                                    }
                                >
                                    <IconButton
                                        icon={mdiCloudUpload}
                                        label="Afbeelding wijzigen"
                                        onClick={() =>
                                            setShowImagePickerModal(true)
                                        }
                                    >
                                        Afbeelding wijzigen
                                    </IconButton>
                                </div>

                                <ImagePickerModal
                                    title="Cover afbeelding"
                                    isOpen={showImagePickerModal}
                                    originalAltText={blockData.alt_text}
                                    originalImage={
                                        imageFileBase64Url ?? media?.image?.url
                                    }
                                    onClose={() =>
                                        setShowImagePickerModal(false)
                                    }
                                    imageWidth={768}
                                    imageHeight={512}
                                    onImageSelected={(image, altText) => {
                                        if (image) setImageFile(image);
                                        if (altText)
                                            setBlockData({
                                                ...blockData,
                                                alt_text: altText,
                                            });
                                    }}
                                />
                            </div>
                        ) : (
                            <>
                                <motion.div
                                    onClick={() =>
                                        setIsFullScreen(!isFullScreen)
                                    }
                                    className="group relative cursor-pointer"
                                    layoutId={
                                        shouldReduceMotion
                                            ? undefined
                                            : `chatimageblock__bubble__image-${uuid}`
                                    }
                                    layoutDependency={showImagePickerModal}
                                >
                                    <ResponsiveImage
                                        imageClassName={`${styles['chatimageblock__bubble__image']}`}
                                        responsiveImages={
                                            imageFileBase64Url
                                                ? undefined
                                                : media?.image
                                                      ?.responsive_images
                                        }
                                        alt={blockData.alt_text}
                                        src={
                                            imageFileBase64Url ??
                                            media?.image?.url
                                        }
                                        onLoaded={() => {
                                            onContentHeightChanged?.();
                                        }}
                                    />
                                    <FullScreenButton
                                        isFullScreen={isFullScreen}
                                        setIsFullScreen={setIsFullScreen}
                                    />
                                </motion.div>

                                <Dialog
                                    open={isFullScreen}
                                    onClose={() => setIsFullScreen(false)}
                                    className="group"
                                >
                                    <motion.div
                                        className="fixed inset-0 z-200 bg-white/30 backdrop-blur-lg"
                                        aria-hidden="true"
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        onClick={() => setIsFullScreen(false)}
                                        layoutDependency={showImagePickerModal}
                                    />

                                    <motion.div
                                        className="fixed inset-0 z-200"
                                        onClick={() => setIsFullScreen(false)}
                                        initial={{
                                            scale: shouldReduceMotion ? 1 : 0.5,
                                            opacity: 0,
                                        }}
                                        animate={{ scale: 1, opacity: 1 }}
                                    >
                                        <div className="fixed inset-0 w-full h-full grid items-stretch md:items-center justify-stretch md:p-8 overflow-y-auto">
                                            <motion.div
                                                className="flex flex-col items-center overflow-hidden md:rounded-2xl shadow-xl bg-slate-800 text-white p-4"
                                                layoutId={
                                                    shouldReduceMotion
                                                        ? undefined
                                                        : `chatimageblock__bubble__image-${uuid}`
                                                }
                                                layoutDependency={
                                                    showImagePickerModal
                                                }
                                            >
                                                <ResponsiveImage
                                                    containerClassName="max-md:mt-auto max-md:w-full"
                                                    imageClassName="rounded-md max-md:w-full max-md:max-h-[90vh] object-contain"
                                                    responsiveImages={
                                                        imageFileBase64Url
                                                            ? undefined
                                                            : media?.image
                                                                  ?.responsive_images
                                                    }
                                                    alt={blockData.alt_text}
                                                    src={
                                                        imageFileBase64Url ??
                                                        media?.image?.url
                                                    }
                                                />
                                                <FullScreenButton
                                                    isFullScreen={isFullScreen}
                                                    setIsFullScreen={
                                                        setIsFullScreen
                                                    }
                                                />
                                                <p className="shrink-0 px-4 pt-4 font-bold">
                                                    {blockData.alt_text}
                                                </p>
                                            </motion.div>
                                        </div>
                                    </motion.div>
                                </Dialog>
                            </>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

interface FullScreenButtonProps {
    isFullScreen: boolean;
    setIsFullScreen: (isFullScreen: boolean) => void;
}

const FullScreenButton = ({
    isFullScreen,
    setIsFullScreen,
}: FullScreenButtonProps) => {
    return (
        <button
            title={!isFullScreen ? 'Groter bekijken' : 'Kleiner maken'}
            className={`absolute top-4 right-4 rounded-full bg-black/50 hover:bg-black/75 focus:bg-black/75 ${
                !isFullScreen ? 'p-2' : 'p-3'
            } text-white opacity-50 transition-all hover:scale-125 hover:shadow-lg active:scale-100 group-focus-within:opacity-100 group-hover:opacity-100`}
            onClick={() => setIsFullScreen(!isFullScreen)}
        >
            <Icon
                path={!isFullScreen ? mdiArrowExpand : mdiArrowCollapse}
                size={!isFullScreen ? 1 : 1.25}
            />
        </button>
    );
};

export default ChatImageBlock;
